import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { orpc } from "@/lib/orpc";
import { useCan, useFeature } from "@/lib/policy";
import { useZone } from "@/lib/facility/zone";
import { ActivityCancelDialog } from "./-components/activity-cancel-dialog";
import { ActivityDetailHeader } from "./-components/activity-detail-header";
import { ActivityDetailsCard } from "./-components/activity-details-card";
import { ActivityDialog } from "./-components/activity-dialog";
import { ActivityEventCard } from "./-components/activity-event-card";
import { ActivityOutcomeDialog } from "./-components/activity-outcome-dialog";
import { AttendanceCard } from "./-components/attendance-card";
import { PrintDialog } from "./-components/print-dialog";
import { RsvpCard } from "./-components/rsvp-card";

export const Route = createFileRoute("/_authenticated/_care/activity-calendar/$activityId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.care.activityCalendar.get.queryOptions({
        input: { activityId: params.activityId },
      }),
    ),
  component: ActivityDetailPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** One activity: what it is, who came, and the Event behind it when the marketers host it. */
function ActivityDetailPage() {
  const { activityId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: activity } = useSuspenseQuery(
    orpc.care.activityCalendar.get.queryOptions({ input: { activityId } }),
  );
  const canWrite = useCan("activity:manage", activity.facilityId);
  const canReadCrm = useCan("crm:read", activity.facilityId);
  const eventsOn = useFeature("crm-events", activity.facilityId);
  const canReadEvents = canReadCrm && eventsOn;
  const showReplies = useFeature("community", activity.facilityId) && activity.isPublic;
  const zone = useZone(activity.facilityId);
  const [editing, setEditing] = useState(false);
  const [copying, setCopying] = useState(false);
  const [recording, setRecording] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: orpc.care.activityCalendar.key(),
    });
  const reopen = useMutation(
    orpc.care.activityCalendar.reopen.mutationOptions({
      onSuccess: refresh,
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.care.activityCalendar.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Activity deleted");
        await refresh();
        await navigate({ to: "/activity-calendar" });
      },
      onError: fail,
    }),
  );

  return (
    <div className="flex flex-col gap-5">
      <ActivityDetailHeader
        activity={activity}
        zone={zone}
        canWrite={canWrite}
        onEdit={() => setEditing(true)}
        onCopy={() => setCopying(true)}
        onOutcome={() => setRecording(true)}
        onCancel={() => setCancelling(true)}
        onReopen={() => reopen.mutate({ activityId: activity.id })}
        onDelete={() => setDeleting(true)}
        onFlyer={() => setPrinting(true)}
      />
      <div className="grid gap-5 lg:grid-cols-2">
        <ActivityDetailsCard activity={activity} />
        <ActivityEventCard activity={activity} canReadEvents={canReadEvents} />
        {showReplies ? (
          <div className="lg:col-span-2">
            <RsvpCard activityId={activity.id} />
          </div>
        ) : null}
        <div className="lg:col-span-2">
          <AttendanceCard activityId={activity.id} canWrite={canWrite} />
        </div>
      </div>

      {editing ? <ActivityDialog activity={activity} onClose={() => setEditing(false)} /> : null}
      {copying ? (
        <ActivityDialog
          activity={null}
          copyOf={activity}
          onClose={() => setCopying(false)}
          onSaved={(saved) =>
            void navigate({
              to: "/activity-calendar/$activityId",
              params: { activityId: saved.id },
            })
          }
        />
      ) : null}
      {recording ? (
        <ActivityOutcomeDialog activity={activity} onClose={() => setRecording(false)} />
      ) : null}
      {cancelling ? (
        <ActivityCancelDialog activity={activity} onClose={() => setCancelling(false)} />
      ) : null}
      {printing ? (
        <PrintDialog
          job={{ kind: "flyer", activityId: activity.id, label: "a flyer" }}
          onClose={() => setPrinting(false)}
        />
      ) : null}
      {deleting ? (
        <RemoveConfirm
          title={`Delete ${activity.title}?`}
          description={
            activity.seriesId
              ? "Only this meeting, planned in error. The rest of the series stays."
              : "Only for one planned in error. A held one, or one with residents marked, is cancelled instead."
          }
          confirmLabel="Delete activity"
          onConfirm={() => remove.mutate({ activityId: activity.id, scope: "one" })}
          onClose={() => setDeleting(false)}
        />
      ) : null}
    </div>
  );
}
